import React, { useEffect } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaArrowRight, FaShieldAlt, FaBolt, FaLock, FaChartLine, FaCheck, FaAngleRight, FaLinkedinIn } from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import { infoPages } from '../config/infoPages';
import './ServiceDetailPage.css';

const icons = [<FaShieldAlt />, <FaBolt />, <FaLock />, <FaChartLine />];

const InfoPage = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, '');
  const page = infoPages[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="svc-page">
      <PageNavbar />

      {/* Hero */}
      <section className="svc-hero">
        <div className="svc-hero-glow" />
        <div className="svc-container svc-hero-inner">
          <motion.div className="svc-hero-text" {...getRevealProps('fadeUp')}>
            <nav className="svc-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaAngleRight />
              <span>{page.title}</span>
            </nav>
            <span className="svc-eyebrow">{page.eyebrow}</span>
            <h1 className="svc-hero-title">{page.title}</h1>
            <p className="svc-hero-desc">{page.description}</p>
            <div className="svc-hero-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                Get In Touch <FaArrowRight />
              </Link>
              <Link to="/services" className="svc-btn svc-btn-ghost">
                Explore Services
              </Link>
            </div>
          </motion.div>

          <motion.aside className="svc-hero-card" {...getRevealProps('slideRight', 0.12)}>
            <span className="svc-card-tag">Highlights</span>
            <ul className="svc-card-list">
              {page.points.map((pt) => (
                <li key={pt.title}>
                  <FaCheck /> {pt.title}
                </li>
              ))}
            </ul>
          </motion.aside>
        </div>
      </section>

      {/* Detail cards */}
      <section className="svc-section">
        <div className="svc-container">
          <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
            <span className="svc-section-tag">{page.sectionTag || 'What to Expect'}</span>
            <h2 className="svc-section-title">{page.title} at Proven InfoSec</h2>
          </motion.header>

          <motion.div
            className="svc-feature-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{
              ...staggerContainer,
              visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
            }}
          >
            {page.points.map((pt, i) => (
              <motion.article className="svc-feature-card" key={pt.title} variants={staggerItem}>
                <div className="svc-feature-icon">{icons[i % icons.length]}</div>
                <h3 className="svc-feature-title">{pt.title}</h3>
                <p className="svc-feature-desc">{pt.desc}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="svc-cta">
        <div className="svc-container">
          <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
            <h2 className="svc-cta-title">{page.ctaTitle || 'Want to know more?'}</h2>
            <p className="svc-cta-desc">
              {page.ctaDesc || 'Talk to our team — we respond to every inquiry within one business day.'}
            </p>
            <div className="svc-cta-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                {page.ctaButtonLabel || 'Book Consultation'} <FaArrowRight />
              </Link>
              {page.externalCta ? (
                <a
                  href={page.externalCta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="svc-btn svc-btn-ghost-light"
                >
                  <FaLinkedinIn /> {page.externalCta.label}
                </a>
              ) : (
                <Link to="/services" className="svc-btn svc-btn-ghost-light">
                  View All Services
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InfoPage;
